import { memo, useMemo } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import ListItemIcon, { listItemIconClasses } from '@mui/material/ListItemIcon';
import ListItemText, { listItemTextClasses } from '@mui/material/ListItemText';
import ListItem from '@mui/material/ListItem';
import { styled } from '@mui/material/styles';
import { listItemSecondaryActionClasses } from '@mui/material';
import Iconify from '../../../components/Iconify';

const StyledListItem = styled(ListItem, {
  shouldForwardProp: prop => prop !== 'isActive' && prop !== 'isDisabled',
})(({ theme, isActive, isDisabled }) => ({
  padding: '8px 16px',
  borderRadius: theme.spacing(1),
  backgroundColor: isActive && !isDisabled ? '#484848' : 'transparent',
  gap: theme.spacing(1),
  ...(isDisabled
    ? {
        pointerEvents: 'none',
        [`& .${listItemIconClasses.root}`]: {
          color: '#4B4B4B',
          minWidth: 'unset',
        },
        [`& .${listItemSecondaryActionClasses.root}`]: {
          '& svg': {
            color: '#4B4B4B',
          },
        },
        '&:hover, &:active': {
          backgroundColor: 'inherit',
          cursor: 'default',
        },
      }
    : {
        [`& .${listItemIconClasses.root}`]: {
          color: isActive ? theme.palette.action.textFocusColor : '#9CA3AF',
          minWidth: 'unset',
        },
        [`& .${listItemSecondaryActionClasses.root}`]: {
          '& svg': {
            color: isActive ? theme.palette.action.textFocusColor : '#9CA3AF',
          },
        },
        ...(!isActive
          ? {
              ':hover': {
                backgroundColor: theme.palette.action.hover,
                [`& .${listItemIconClasses.root}`]: {
                  '& svg': {
                    color: '#DDDDDD',
                  },
                },
                [`& .${listItemSecondaryActionClasses.root}`]: {
                  '& svg': {
                    color: '#DDDDDD',
                  },
                },
              },
              ':active': {
                backgroundColor: theme.palette.action.focus,
                [`& .${listItemIconClasses.root}`]: {
                  '& svg': {
                    color: theme.palette.action.textFocusColor,
                  },
                },
                [`& .${listItemSecondaryActionClasses.root}`]: {
                  '& svg': {
                    color: theme.palette.action.textFocusColor,
                  },
                },
              },
            }
          : {}),
      }),
  '& .MuiListItemSecondaryAction-root': {
    display: 'flex',
    position: 'initial',
    transform: 'initial',
  },
}));

const StyledListItemText = styled(ListItemText, {
  shouldForwardProp: prop => prop !== 'isActive' && prop !== 'isDisabled',
})(({ theme, isActive, isDisabled }) => ({
  [`& .${listItemTextClasses.primary}`]: {
    fontSize: theme.typography.pxToRem(10),
    lineHeight: '24px',
    fontFamily: 'Inter',
    fontWeight: 600,
    color: isDisabled ? '#4B4B4B' : isActive ? theme.palette.action.textFocusColor : '#D1D5DB',
    ...(!isActive && !isDisabled
      ? {
          ':hover': {
            color: theme.palette.action.textHoverColor,
          },
          ':active': {
            color: theme.palette.action.textFocusColor,
          },
        }
      : {}),
  },
}));

const NavigationItem = ({
  icon,
  title,
  isActive = false,
  path = null,
  disabled,
  query,
  externalLink = false,
  onClick,
  canNavigate,
  count,
  ...props
}) => {
  const to = useMemo(() => {
    if (!path) return null;
    return {
      pathname: path,
      search: query,
    };
  }, [path, query]);

  return (
    <StyledListItem
      {...props}
      onClick={onClick}
      isDisabled={disabled}
      isActive={isActive}
      secondaryAction={
        <Iconify
          icon="iconamoon:arrow-right-2-duotone"
          color={disabled ? '#4B4B4B' : '#DDDDDD'}
          width={24}
          height={24}
        />
      }
      component={path ? RouterLink : 'div'}
      to={to}
      state={path ? { prevPath: window.location.pathname, canNavigate } : undefined}
      reloadDocument={externalLink}>
      <ListItemIcon>
        <Iconify
          icon={icon}
          sx={theme => ({
            color: isActive ? theme.palette.action.textFocusColor : '#9CA3AF',
          })}
          width={24}
          height={24}
        />
      </ListItemIcon>
      <StyledListItemText
        primary={`${title} ${count ? `(${count})` : ''}`}
        isActive={isActive}
        sx={{ margin: 0 }}
        isDisabled={disabled}
      />
    </StyledListItem>
  );
};

export default memo(NavigationItem);
